import { IRequestService } from '../iRequestService';
import { ServiceRequest, Payout, Route, Particular, Invoice, User } from '@/types/entity';
import { QueryRecordsRequest, ApiRequest, GetServiceRequestsInput, CompleteRequest } from '@/types/request';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from '@/util/constants';
import { ApiResponse } from '@/types/response';
import { DateUtils } from '@/util/dateUtils';
import { CommonUtils } from '@/util/commonUtils';
import { ToastEvent } from '@/types/event';
import { EventBus } from '@/main';
import { MathUtils } from '@/util/mathUtils';


export class RequestService implements IRequestService {

    public async getRequests(getServiceRequestsInput: GetServiceRequestsInput):
        Promise<ApiResponse<ServiceRequest[]>> {
        const queryRecordsRequest: QueryRecordsRequest = {
            conditions: getServiceRequestsInput.conditions ? getServiceRequestsInput.conditions : [],
            order: {
                createdOn: -1,
            },
            limit: getServiceRequestsInput.limit || 10,
            start: getServiceRequestsInput.start,
            serviceType: getServiceRequestsInput.service,
        };
        if (getServiceRequestsInput.service && queryRecordsRequest.conditions) {
            queryRecordsRequest.conditions.push({
                op: 'ne',
                fieldName: getServiceRequestsInput.service,
                value: null,
            });
        }
        if (getServiceRequestsInput.advanceSearchRequest && queryRecordsRequest.conditions) {
            const advancedSearchRequest = getServiceRequestsInput.advanceSearchRequest;
            if (advancedSearchRequest.appointmentDate) {
                if (advancedSearchRequest.appointmentDate.startDate) {
                    const startDate = DateUtils.convertToUTCInMillis(advancedSearchRequest.appointmentDate.startDate);
                    queryRecordsRequest.conditions.push({
                        fieldName: 'appointment.timestamp',
                        op: 'gte',
                        value: startDate,
                    });
                }
                if (advancedSearchRequest.appointmentDate.endDate) {
                    const endDate = DateUtils.convertToUTCInMillis(advancedSearchRequest.appointmentDate.endDate);
                    queryRecordsRequest.conditions.push({
                        fieldName: 'appointment.timestamp',
                        op: 'lte',
                        value: endDate,
                    });
                }
                if (advancedSearchRequest.status && advancedSearchRequest.status.length > 0) {
                    queryRecordsRequest.conditions.push({
                        fieldName: 'status',
                        op: 'in',
                        value: advancedSearchRequest.status,
                    });
                }
                if (advancedSearchRequest.patientName) {
                        queryRecordsRequest.conditions.push({
                            fieldName: 'appointment.patient.fullName',
                            op: 'eq',
                            value: advancedSearchRequest.patientName,
                        });
                }
                if (advancedSearchRequest.searchString) {
                    queryRecordsRequest.conditions.push({
                        fieldName: 'requestId',
                        op: 'eq',
                        value: advancedSearchRequest.searchString,
                    });
                }
            }
        }
        if (getServiceRequestsInput.status && queryRecordsRequest.conditions) {
            queryRecordsRequest.conditions.push({
                op: 'eq',
                fieldName: 'status',
                value: getServiceRequestsInput.status,
            });
        }
        const request: ApiRequest = {
            baseURL: getServiceRequestsInput.baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS}/list`,
            data: queryRecordsRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await ObjectFactory.getHttpService().post<ServiceRequest[]>(request);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        await this.getFormattedRequests(response.data as any, getServiceRequestsInput.baseURL);
        return response;
    }

    public async getRequestDetails(requestId: string, baseURL: string): Promise<ApiResponse<any>> {
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS}/${requestId}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await ObjectFactory.getHttpService().get(apiRequest);
        if (response.success && response.data) {
            const serviceRequest = response.data;
            if (serviceRequest.transportRequest && serviceRequest.interpreterRequest) {
                serviceRequest.serviceType = 'Transportation & Interpretation';
            } else if (serviceRequest.interpreterRequest && serviceRequest.telemedicineRequest) {
                serviceRequest.serviceType = `Telemedicine & Interpretation (${serviceRequest.interpreterRequest.serviceType})`;
            } else if (serviceRequest.telemedicineRequest) {
                serviceRequest.serviceType = 'Telemedicine';
            } else if (serviceRequest.transportRequest) {
                serviceRequest.serviceType = 'Transportation';
            } else if (serviceRequest.homeHealthCareRequest) {
                serviceRequest.serviceType = 'HomeHealthCareRequest';
            } else if (serviceRequest.deliveryRequest) {
                serviceRequest.serviceType = 'Medication Delivery';
            } else if (serviceRequest.interpreterRequest) {
                serviceRequest.serviceType = `Interpretation (${serviceRequest.interpreterRequest.serviceType})`;
            }
            if (serviceRequest.transportRequest) {
                serviceRequest.transportRequest.mapRoutes =
                    await this.getRoutes(serviceRequest.transportRequest.routes, baseURL);
                serviceRequest.transportRequest.totalDistance = 0;
                serviceRequest.transportRequest.mapRoutes.forEach((element: any) => {
                    if (element.params && element.params.waitingTime) {
                        element.params.waitingTime = DateUtils.getDurationText(DateUtils.convertMillisToMinutes(
                            element.params.waitingTime)) as any;
                    }
                    if (serviceRequest.transportRequest && element.totalMiles) {
                        serviceRequest.transportRequest.totalDistance += element.totalMiles;
                    }
                });
            }
            if (serviceRequest.deliveryRequest) {
                serviceRequest.deliveryRequest.mapRoutes =
                    await this.getRoutes(serviceRequest.deliveryRequest.routes, baseURL);
                serviceRequest.deliveryRequest.mapRoutes.forEach((element: any) => {
                        if (element.params && element.params.waitingTime) {
                            element.params.waitingTime = DateUtils.getDurationText(DateUtils.convertMillisToMinutes(
                                element.params.waitingTime)) as any;
                        }
                    });
            }
        }
        return response;
    }

    public async rejectRequest(requestId: string, baseURL: string,
                               reason?: string, partnerAvailabilityTime?: string): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS}/${requestId}/reject`,
            data: {
                reason,
                partnerAvailabilityTime,
            },
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<string>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'This request has been removed from your list',
                title: 'Request Rejected!',
                varient: 'success',
                timeout: 5000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async acceptRequest(requestId: string, baseURL: string, dataBody?: any): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            data: dataBody,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS}/${requestId}/assign`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<string>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getPayouts(getPayoutsRequest: QueryRecordsRequest, baseURL: string): Promise<ApiResponse<Payout[]>> {
        const queryRecordsRequest: QueryRecordsRequest = {
            conditions: getPayoutsRequest.conditions ? getPayoutsRequest.conditions : [],
            order: {
                createdOn: -1,
            },
            start: getPayoutsRequest.start,
            limit: getPayoutsRequest.limit,
        };
        if (status && queryRecordsRequest.conditions) {
            queryRecordsRequest.conditions.push({
                op: 'eq',
                fieldName: 'status',
                value: status,
            });
        }
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_PAYOUTS}/list`,
            data: queryRecordsRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await ObjectFactory.getHttpService().post<Payout[]>(apiRequest);
        if (response.success && response.data) {
            response.data.forEach((element: any) => {
                element.amount = MathUtils.formatCurrency(element.amount);
                element.createdOn = DateUtils.formatDate(element.createdOn);
            });
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async completeRequest(requestId: string, completeRequest: CompleteRequest,
                                 baseURL: string): Promise<ApiResponse<any>> {
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS}/${requestId}/complete`,
            data: completeRequest,
            timeout: 50000,
            loaderEvent: {
                show: true,
            },
        };
        const response = await ObjectFactory.getHttpService().put<any>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'Request has been successfully completed',
                title: 'Success',
                varient: 'success',
                timeout: 2000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async refreshToken(clientId: string, baseURL: string): Promise<ApiResponse<string>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_USERS}/refreshToken?clientId=${clientId}`,
        };
        const response = await iHttpService.put<string>(apiRequest);
        return response;
    }

    public async getUserById(id: string, baseURL: string): Promise<ApiResponse<User>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_USERS}/${id}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<User>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getRequestInvoice(invoiceId: string, baseURL: string): Promise<ApiResponse<Invoice>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_INVOICES}/${invoiceId}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<Invoice>(apiRequest);
        if (response.success && response.data) {
            response.data.totalAmount = 0;
            response.data.particulars.forEach((element: Particular) => {
                if (response.data) {
                    response.data.totalAmount += element.amount;
                }
            });
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    private async getFormattedRequests(result: ServiceRequest[], baseURL: string) {
        if (result) {
            result.forEach(async (element: ServiceRequest) => {
                element.appointment.date = DateUtils.formatDate(
                    element.appointment.date,
                    DateUtils.FORMAT_DATETIME_AM_PM,
                ) as any;
                element.createdOn = DateUtils.formatDate(
                    element.createdOn,
                    DateUtils.FORMAT_DATETIME_AM_PM,
                ) as any;
                if (element.interpreterRequest) {
                    const hours = element.interpreterRequest.estimatedTime.hours;
                    const minutes = element.interpreterRequest.estimatedTime.minutes;
                    element.interpreterRequest.estimatedTime = `${hours}:${minutes} Hrs` as any;
                }
            });
        }
    }

    private async getRoutes(routes: Route[], baseURL: string): Promise<Route[]> {
        return new Promise<Route[]>((resolve, reject) => {
            if (routes) {
                const mapRoutes: Route[] = [];
                const promises: Array<Promise<ApiResponse<Route>>> = [];
                routes.forEach((element) => {
                    const routeId = element.routeId;
                    const apiRequest: ApiRequest = {
                        baseURL,
                        url: `${MedTransGoConstants.HTTP_URL_ROUTES}/${routeId}`,
                        loaderEvent: {
                            show: true,
                        },
                    };
                    const iHttpService = ObjectFactory.getHttpService();
                    const requestPromise = iHttpService.get<Route>(apiRequest);
                    promises.push(requestPromise);
                });
                Promise.all(promises).then((responses) => {
                    let index = 0;
                    responses.forEach((response) => {
                        if (response.success && response.data) {
                            const route = response.data;
                            const origin = {
                                lat: route.pickupAddress.latitude,
                                lng: route.pickupAddress.longitude, icon: 'ambulance-icon.png',
                            };
                            const destination = {
                                lat: route.dropoffAddress.latitude,
                                lng: route.dropoffAddress.longitude, icon: 'hospital-icon.png',
                            };
                            const stops = {
                                lat: route.dropoffAddress.latitude,
                                lng: route.dropoffAddress.longitude, icon: 'rx-icon.png',
                            };
                            const googleMapRoute: any = {
                                destination,
                                markers: [],
                                origin,
                                stops,
                            };
                            googleMapRoute.markers.push({ position: origin });
                            if (route.additionalStops && route.additionalStops.length > 0) {
                                googleMapRoute.additionalStops = route.additionalStops;
                                route.additionalStops.forEach((element) => {
                                    const stop = {
                                        lat: element.address.latitude,
                                        lng: element.address.longitude,
                                        icon: 'rx-icon.png',
                                    };
                                    googleMapRoute.markers.push({ position: stop });
                                });
                            }
                            googleMapRoute.markers.push({ position: destination });
                            if (index++ > 1) {
                                destination.icon = 'home-icon.png';
                            }
                            route.googleMap = googleMapRoute;
                            mapRoutes.push(route);
                        } else {
                            CommonUtils.showError(response);
                        }
                    });
                    resolve(mapRoutes);
                }).catch(() => {
                    reject('Failed to get route');
                });
            }
        });
    }

}
